import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  message,
  InputNumber,
} from "antd";
import {
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/en_US";
import { format } from "date-fns";
import logger from "../../utils/logger";

const { Option } = Select;
const { TextArea } = Input;

const InitialPaymentModal = ({
  visible,
  onClose,
  onSave,
  clientData,
  caseData,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && caseData) {
      form.setFieldsValue({
        amount: caseData.agreed_fees || 0,
        description: `دفعة أولية للقضية: ${caseData.title || ""} (${
          caseData.case_number || ""
        })`,
        payment_date: dayjs(),
        payment_type: "case",
        status: "completed",
      });
    }
  }, [visible, caseData, form]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const paymentData = {
        amount: parseFloat(values.amount || 0).toFixed(2),
        payment_date: values.payment_date
          ? format(new Date(values.payment_date), "yyyy-MM-dd")
          : format(new Date(), "yyyy-MM-dd"),
        description: values.description || "",
        client_id: clientData?.id,
        client_name: clientData?.name,
        // بيانات القضية المؤقتة
        temp_case_data: {
          title: caseData?.title,
          case_number: caseData?.case_number,
          agreed_fees: caseData?.agreed_fees,
          client_name: caseData?.client_name,
          client_id: caseData?.client_id,
        },
      };

      // استدعاء onSave لحفظ البيانات مؤقتاً
      if (onSave) {
        onSave(paymentData);
      }

      message.success("تم حفظ بيانات الدفعة الأولية");
      form.resetFields();
      onClose();
    } catch (error) {
      logger.error("Error saving initial payment:", error);
      message.error("حدث خطأ في حفظ بيانات الدفعة");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div className="text-xl font-bold text-gray-800 mb-4 flex items-center justify-end">
          <span className="ml-2">إضافة دفعة أولية</span>
          <DollarOutlined className="text-primary" />
        </div>
      }
      open={visible}
      onCancel={onClose}
      okText="حفظ"
      cancelText="إلغاء"
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={500}
      dir="rtl"
      className="custom-modal"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
        dir="rtl"
      >
        {/* معلومات العميل والقضية */}
        <div
          className="bg-blue-50 p-3 rounded-lg mb-4"
          style={{ direction: "rtl", textAlign: "right" }}
        >
          <div className="mb-2">
            <span className="font-medium text-blue-700">العميل: </span>
            <span className="text-sm text-blue-800">{clientData?.name || "غير محدد"}</span>
          </div>
          <div>
            <span className="font-medium text-blue-700">القضية: </span>
            <span className="text-sm text-blue-800">
              {caseData?.title || "غير محدد"} ({caseData?.case_number || "غير محدد"})
            </span>
          </div>
        </div>

        {/* المبلغ */}
        <Form.Item
          name="amount"
          label="المبلغ"
          rules={[
            { required: true, message: "المبلغ مطلوب" },
            {
              type: "number",
              min: 1,
              transform: (value) => Number(value),
              message: "المبلغ يجب أن يكون أكبر من صفر",
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%", textAlign: "right" }}
            placeholder="أدخل المبلغ"
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
            suffix="د.ع"
          />
        </Form.Item>

        {/* التاريخ */}
        <Form.Item
          name="payment_date"
          label="التاريخ"
          rules={[{ required: true, message: "التاريخ مطلوب" }]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="YYYY/MM/DD"
            locale={locale}
            className="w-full"
            placeholder="اختر التاريخ"
          />
        </Form.Item>

        {/* الوصف */}
        <Form.Item name="description" label="ملاحظات (اختياري)">
          <TextArea
            placeholder="ملاحظات عن الدفعة..."
            rows={2}
            style={{ textAlign: "right" }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default InitialPaymentModal;
